import { useState } from 'react'
import { PlusLg, ArrowRepeat } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel, FormModal, RowActions, CommitInput } from '../components/ui.jsx'
import { entries, accountBalance, netWorth, snapshotSeries } from '../lib/finance.js'
import { money, dateLabel, todayISO } from '../lib/format.js'
import { ACCOUNT_CATEGORIES, ACCOUNT_SOURCES } from '../lib/constants.js'
import { add, setAt, updateAt, removeAt } from '../lib/firebase.js'

const FIELDS = [
  { name: 'name', label: 'Name', required: true, placeholder: 'e.g. Revolut – Savings' },
  { name: 'institution', label: 'Institution', required: true, col: 'col-6', placeholder: 'Revolut, Gránit, Cash…' },
  { name: 'category', label: 'Category', type: 'select', required: true, col: 'col-6', options: ACCOUNT_CATEGORIES },
  { name: 'source', label: 'Balance source', type: 'select', required: true, options: ACCOUNT_SOURCES,
    help: 'Synced accounts take their balance from the Lightyear & Coinbase page.' },
  { name: 'balance', label: 'Balance', type: 'number', step: '0.01', hidden: (v) => v.source && v.source !== 'manual' },
]

export default function Accounts() {
  const data = useData()
  const [edit, setEdit] = useState(null)
  const [noteFor, setNoteFor] = useState(null)
  const accounts = entries(data.accounts).sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name))
  const institutions = [...new Set(accounts.map((a) => a.institution || 'Other'))]
  const nw = netWorth(data, todayISO())
  const bal = (a) => accountBalance(a, data.integrations)

  const matrix = ACCOUNT_CATEGORIES.map((c) => {
    const row = { ...c, total: 0, cells: {} }
    for (const i of institutions) {
      const v = accounts.filter((a) => a.category === c.id && (a.institution || 'Other') === i).reduce((s, a) => s + bal(a), 0)
      row.cells[i] = v; row.total += v
    }
    return row
  })

  const history = snapshotSeries(data.snapshots).reverse()
  // Debt accrues interest daily, so only days where assets changed (or with a note) are listed.
  const changes = history.filter((s, i) => i === history.length - 1 || s.assets !== history[i + 1].assets || data.snapshots[s.date]?.note).slice(0, 60)

  async function save(values) {
    const { id, ...rest } = edit
    const rec = { ...rest, ...values, updatedAt: todayISO() }
    if (id) await setAt(`accounts/${id}`, rec)
    else await add('accounts', { ...rec, order: accounts.length })
  }

  return (
    <>
      <PageHeader title="Balances" subtitle="Update balances here; today's net-worth snapshot follows automatically.">
        <button className="btn btn-primary" onClick={() => setEdit({ source: 'manual', category: 'cash' })}><PlusLg /> Account</button>
      </PageHeader>

      <Panel title="Overview" className="mb-3">
        <div className="table-wrap"><table className="table">
          <thead><tr><th /><th className="text-end">Total</th>{institutions.map((i) => <th key={i} className="text-end">{i}</th>)}</tr></thead>
          <tbody>
            {matrix.map((r) => (
              <tr key={r.id}><td>{r.label}</td><td className="num fw-semibold">{money(r.total)}</td>
                {institutions.map((i) => <td key={i} className="num text-body-secondary">{r.cells[i] ? money(r.cells[i]) : ''}</td>)}</tr>
            ))}
            <tr className="fw-bold"><td>Total</td><td className="num">{money(nw.assets)}</td>
              {institutions.map((i) => <td key={i} className="num">{money(matrix.reduce((s, r) => s + r.cells[i], 0))}</td>)}</tr>
            <tr><td>Debt</td><td className="num neg">{money(-nw.debt)}</td><td colSpan={institutions.length} /></tr>
            <tr className="fw-bold"><td>Net worth</td><td className={`num ${nw.net < 0 ? 'neg' : ''}`}>{money(nw.net)}</td><td colSpan={institutions.length} /></tr>
          </tbody>
        </table></div>
      </Panel>

      <Panel title="Accounts" className="mb-3">
        {accounts.length ? (
          <div className="table-wrap"><table className="table align-middle">
            <thead><tr><th>Name</th><th>Institution</th><th>Category</th><th>Updated</th><th className="text-end" style={{ width: 170 }}>Balance</th><th /></tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td className="small">{a.institution}</td>
                  <td className="small">{ACCOUNT_CATEGORIES.find((c) => c.id === a.category)?.label}</td>
                  <td className="small text-body-secondary">{a.source !== 'manual' ? <><ArrowRepeat /> {a.source}</> : a.updatedAt ? dateLabel(a.updatedAt) : ''}</td>
                  <td className="text-end">
                    {a.source === 'manual'
                      ? <CommitInput value={a.balance} onSave={(v) => updateAt(`accounts/${a.id}`, { balance: v, updatedAt: todayISO() })} />
                      : <span className="num">{money(bal(a))}</span>}
                  </td>
                  <td className="text-end"><RowActions onEdit={() => setEdit(a)} onDelete={() => removeAt(`accounts/${a.id}`)} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ) : <div className="empty">No accounts yet. Add one, or import your spreadsheet (see Settings).</div>}
      </Panel>

      <Panel title="Net-worth history (balance changes)">
        {changes.length ? (
          <div className="table-wrap"><table className="table table-sm align-middle">
            <thead><tr><th>Date</th><th className="text-end">Assets</th><th className="text-end">Debt</th><th className="text-end">Net</th><th>Note</th><th /></tr></thead>
            <tbody>
              {changes.map((s) => (
                <tr key={s.date}>
                  <td className="small text-nowrap">{dateLabel(s.date)}</td>
                  <td className="num">{money(s.assets)}</td><td className="num">{money(s.debt)}</td>
                  <td className={`num fw-semibold ${s.net < 0 ? 'neg' : ''}`}>{money(s.net)}</td>
                  <td className="small">{data.snapshots[s.date]?.note}</td>
                  <td className="text-end"><RowActions onEdit={() => setNoteFor(s.date)} onDelete={s.date !== todayISO() ? () => removeAt(`snapshots/${s.date}`) : null} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ) : <div className="empty">No history yet.</div>}
      </Panel>

      {edit && <FormModal title={edit.id ? 'Edit account' : 'New account'} fields={FIELDS} initial={edit} onSubmit={save} onClose={() => setEdit(null)} />}
      {noteFor && <FormModal title={`Snapshot · ${dateLabel(noteFor)}`} initial={data.snapshots[noteFor]}
        fields={[
          { name: 'assets', label: 'Assets', type: 'number', required: true, col: 'col-6' },
          { name: 'debt', label: 'Debt', type: 'number', required: true, col: 'col-6' },
          { name: 'note', label: 'Note', type: 'text' },
        ]}
        onSubmit={(v) => updateAt(`snapshots/${noteFor}`, v)} onClose={() => setNoteFor(null)} />}
    </>
  )
}
